import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'
import * as Styled from './AudioPlayer.styled'
import ReactPlayer from 'react-player'
import { useSelector } from 'react-redux'
import { RootState } from 'reducers'
import usePlayer from 'hooks/usePlayer'
import parseSecondsToText from '../../utils/parseSecondsToText'
import { updatePlayedTime } from 'services/api'
import { AuthContext } from 'authentication/context'

const PROGRESS_INTERVAL = 1000

export interface AudioPlayerProps {}

const AudioPlayer: React.FC<AudioPlayerProps> = () => {
  const playerRef = useRef<ReactPlayer>(null)
  const [speed, setSpeed] = useState<string>('1')
  const [duration, setDuration] = useState(0)
  const [totalPlayedTime, setTotalPlayedTime] = useState(0)
  const playerController = usePlayer()
  const player = useSelector((state: RootState) => state.playerReducer)

  const { user } = useContext(AuthContext) as {
    user: any
  }

  useEffect(() => {
    if (player.currentEpisode.currentTime) {
      playerRef.current.seekTo(player.currentEpisode.currentTime)
    }
  }, [player.currentEpisode.currentAudioUrl])

  const skipTime = (timeToSkip: number) => {
    playerRef.current?.seekTo(
      (playerRef.current ? playerRef.current.getCurrentTime() : 0) + timeToSkip,
      'seconds',
    )
    playerController.updateCurrentTime(playerRef.current.getCurrentTime())
  }

  const skipToTime = (timeToSkip: number) => {
    playerRef.current?.seekTo(timeToSkip, 'seconds')
    playerController.updateCurrentTime(playerRef.current.getCurrentTime())
  }

  const handleDuration = (value: number): void => {
    setDuration(value)
  }

  const handleProgress = (state): void => {
    playerController.updateCurrentTime(state.playedSeconds)
    setTotalPlayedTime((prevState) => prevState + PROGRESS_INTERVAL / 1000)

    updatePlayedTime(player.currentEpisode.currentEpisodeId, {
      currentTime: state.playedSeconds,
      duration: duration,
      totalPlayedTime: totalPlayedTime,
    })
  }

  const handleVolumeChange = useCallback(
    (_: React.ChangeEvent<{}>, newVolume: number | number[]): void => {
      if (player.isMuted) playerController.toggleMute(false)
      playerController.setVolume(+newVolume)
    },
    [player.isMuted, playerController],
  )

  useEffect(() => {
    if (!user) {
      playerController.pause()
    }
  }, [user])

  return (
    <>
      <Styled.Container>
        <Styled.BasePlayer
          ref={playerRef}
          url={player.currentEpisode.currentAudioUrl}
          file={{ forceAudio: true }}
          controls
          playing={player.isPlaying && !!user}
          progressInterval={PROGRESS_INTERVAL}
          onProgress={(state) => {
            handleProgress(state)
          }}
          onDuration={handleDuration}
          width={0}
          height={0}
          playbackRate={Number(speed)}
          volume={player.volume / 100}
          muted={player.isMuted}
        />
        <Styled.TimeSlider
          onChange={(event, newValue) => {
            skipToTime(Number(newValue))
          }}
          max={duration}
          value={player.currentEpisode.currentTime}
        />
        <Styled.Content>
          <Styled.InfoColumn flex={1}>
            <Styled.Row>
              <Styled.ImageWrapper>
                <img src={player.currentEpisode.thumbnail} />
              </Styled.ImageWrapper>
              <Styled.Column>
                <Styled.Title>{player.currentEpisode.title}</Styled.Title>
                <Styled.PodcastTitle>
                  {player.currentEpisode.podcastTitle}
                </Styled.PodcastTitle>
                <Styled.TextInfo>{`${parseSecondsToText(
                  player.currentEpisode.currentTime,
                )}/${parseSecondsToText(duration)}`}</Styled.TextInfo>
              </Styled.Column>
            </Styled.Row>
          </Styled.InfoColumn>
          <Styled.Column>
            <Styled.Controllers>
              <Styled.Row>
                <Styled.Row align="center">
                  <Styled.SkipBack onClick={() => skipTime(-15)} />
                  {!player.isPlaying ? (
                    <Styled.Play onClick={() => playerController.play()} />
                  ) : (
                    <Styled.Pause onClick={() => playerController.pause()} />
                  )}
                  <Styled.SkipForward onClick={() => skipTime(15)} />
                </Styled.Row>
                <Styled.RateSelect
                  className="rate-select"
                  value={speed}
                  onChange={(event: any) => setSpeed(event?.target?.value)}
                >
                  <Styled.RateSelectItem value="0.5">
                    0.5x
                  </Styled.RateSelectItem>
                  <Styled.RateSelectItem value="0.75">
                    0.75x
                  </Styled.RateSelectItem>
                  <Styled.RateSelectItem value="1">1.0x</Styled.RateSelectItem>
                  <Styled.RateSelectItem value="1.25">
                    1.25x
                  </Styled.RateSelectItem>
                  <Styled.RateSelectItem value="1.5">
                    1.5x
                  </Styled.RateSelectItem>
                  <Styled.RateSelectItem value="1.75">
                    1.75x
                  </Styled.RateSelectItem>
                  <Styled.RateSelectItem value="2">2x</Styled.RateSelectItem>
                </Styled.RateSelect>
              </Styled.Row>
            </Styled.Controllers>
          </Styled.Column>
          <Styled.Column>
            <Styled.VolumeAndCloseRow>
              <Styled.VolumeColumn>
                <Styled.VolumeRow>
                  <Styled.MuteButton
                    onClick={() => playerController.toggleMute()}
                    muted={player.isMuted}
                  />
                  <Styled.VolumeSlider
                    max={100}
                    value={player.isMuted ? 0 : player.volume}
                    onChange={handleVolumeChange}
                    valueLabelDisplay="auto"
                  />
                </Styled.VolumeRow>
              </Styled.VolumeColumn>

              <Styled.CloseColumn>
                <Styled.CloseIcon onClick={() => playerController.hide()} />
              </Styled.CloseColumn>
            </Styled.VolumeAndCloseRow>
          </Styled.Column>
        </Styled.Content>
      </Styled.Container>
    </>
  )
}

export default AudioPlayer
